import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _typeof from '@babel/runtime/helpers/typeof';
import { reject, isNil, map, mergeRight, chain, o, toPairs, toUpper, concat, pluck, path } from 'ramda';
import { DEFAULT_FEATURE } from '@redux-syringe/namespaces';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import invariant from 'invariant';
import { withoutOnce, noop, capitalize } from '@redux-syringe/utils';
var createPathValueEntries = function createPathValueEntries(injectables) {
  if (!injectables) {
    return [];
  }
  if (typeof injectables === 'function') {
    return [{
      path: [],
      value: injectables
    }];
  }
  if (Array.isArray(injectables)) {
    return chain(createPathValueEntries, injectables);
  }
  if (_typeof(injectables) === 'object') {
    var createEntriesFromPair = function createEntriesFromPair(pair) {
      var entries = createPathValueEntries(pair[1]);
      var prependEntryPath = function prependEntryPath(entry) {
        return _objectSpread(_objectSpread({}, entry), {}, {
          path: [pair[0]].concat(_toConsumableArray(entry.path))
        });
      };
      return map(prependEntryPath, entries);
    };
    return o(chain(createEntriesFromPair), toPairs)(injectables);
  }
  return [];
};
/**
 * Converts the input of `store.injectSomething()` or `store.ejectSomething()`
 * to an array of standalone entries.
 */

var createEntries = function createEntries(injectables) {
  var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
    feature = _ref.feature,
    namespace = _ref.namespace;
  var sanitizedProps = reject(isNil, {
    namespace: namespace,
    feature: namespace ? feature !== null && feature !== void 0 ? feature : DEFAULT_FEATURE : undefined
  });
  var rawEntries = createPathValueEntries(injectables);
  var addPropsToEntries = map(mergeRight(sanitizedProps));
  return addPropsToEntries(rawEntries);
};
var enhanceStore = function enhanceStore(prevStore, storeInterface) {
  var _objectSpread2;
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
    _ref$onEjected = _ref.onEjected,
    onEjected = _ref$onEjected === void 0 ? noop : _ref$onEjected,
    _ref$onInjected = _ref.onInjected,
    onInjected = _ref$onInjected === void 0 ? noop : _ref$onInjected;
  invariant(prevStore && _typeof(prevStore) === 'object', 'You must pass a Redux store as the first argument to `enhanceStore()`');
  invariant(prevStore && _typeof(prevStore) === 'object', 'You must pass a store interface as the second argument to `enhanceStore()`');
  var injectionKey = storeInterface.injectionKey,
    ejectionKey = storeInterface.ejectionKey,
    getEntries = storeInterface.getEntries,
    setEntries = storeInterface.setEntries,
    type = storeInterface.type;
  var _prevStore$dispatch = prevStore.dispatch,
    dispatch = _prevStore$dispatch === void 0 ? noop : _prevStore$dispatch;
  var actionType = toUpper(type);
  var inject = function inject(injectables) {
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var entries = createEntries(injectables, props);
    var nextEntries = concat(getEntries(nextStore), entries);
    setEntries(nextEntries, nextStore);
    onInjected({
      injectables: injectables,
      props: props,
      entries: entries
    });
    dispatch({
      type: "@redux-syringe/".concat(actionType, "_INJECTED"),
      payload: pluck('path', entries),
      meta: props
    });
  };
  var eject = function eject(injectables) {
    var props = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var entries = createEntries(injectables, props);
    var nextEntries = withoutOnce(entries, getEntries(nextStore));
    setEntries(nextEntries, nextStore);
    onEjected({
      injectables: injectables,
      props: props,
      entries: entries
    });
    dispatch({
      type: "@redux-syringe/".concat(actionType, "_EJECTED"),
      payload: pluck('path', entries),
      meta: props
    });
  };
  var nextStore = _objectSpread(_objectSpread({}, prevStore), {}, (_objectSpread2 = {}, _defineProperty(_objectSpread2, injectionKey, inject), _defineProperty(_objectSpread2, ejectionKey, eject), _objectSpread2));
  setEntries([], nextStore);
  return nextStore;
};

/* eslint-disable no-param-reassign */
var makeStoreInterface = function makeStoreInterface(type) {
  invariant(type, 'The type of the injectables must be defined.');
  return {
    type: type,
    injectionKey: "inject".concat(capitalize(type)),
    ejectionKey: "eject".concat(capitalize(type)),
    getEntries: o(function (entries) {
      return entries !== null && entries !== void 0 ? entries : [];
    }, path(['entries', type])),
    setEntries: function setEntries(entries, store) {
      if (!store.entries) {
        store.entries = {};
      }
      store.entries[type] = entries;
    }
  };
};
export { createEntries, enhanceStore, makeStoreInterface };